'use client';

import { getCmsLocalizedHref } from '@/cms/chrome/localized-href';
import {
  type SiteRuntimeElementDataAttributes,
  SiteRuntimeTargetTrigger,
} from '@/site-runtime/components/site-runtime-entry-action-controller';
import type { SiteRuntimeTarget } from '@/site-runtime/runtime-config-contract';
import Link from 'next/link';
import type { ReactNode } from 'react';

export type CmsSiteChromeEditTargetAttributes =
  SiteRuntimeElementDataAttributes;

export type CmsSiteChromeTarget =
  | {
      kind: 'cms-href';
      href: string;
      locale: string;
    }
  | {
      kind: 'runtime-target';
      target: SiteRuntimeTarget;
    };

function isExternalHref(href: string) {
  return (
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#')
  );
}

export function getCmsSiteChromeTargetHref(
  target: CmsSiteChromeTarget | undefined
) {
  if (!target) {
    return undefined;
  }

  if (target.kind === 'cms-href') {
    return target.href;
  }

  return target.target.type === 'open_url' ? target.target.href : undefined;
}

export function cmsSiteChromeTargetCanNavigate(
  target: CmsSiteChromeTarget | undefined
) {
  if (!target) {
    return false;
  }

  return target.kind === 'cms-href' || target.target.type !== 'none';
}

export function CmsSiteChromeTargetTrigger({
  ariaLabel,
  children,
  className,
  dataAttributes,
  onClick,
  target,
}: {
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  dataAttributes?: CmsSiteChromeEditTargetAttributes;
  onClick?: () => void;
  target?: CmsSiteChromeTarget;
}) {
  if (!target) {
    return (
      <span {...dataAttributes} aria-label={ariaLabel} className={className}>
        {children}
      </span>
    );
  }

  if (target.kind === 'runtime-target') {
    return (
      <SiteRuntimeTargetTrigger
        className={className}
        dataAttributes={
          ariaLabel
            ? { ...dataAttributes, 'aria-label': ariaLabel }
            : dataAttributes
        }
        onClick={onClick}
        target={target.target}
      >
        {children}
      </SiteRuntimeTargetTrigger>
    );
  }

  const href = isExternalHref(target.href)
    ? target.href
    : getCmsLocalizedHref(target.locale, target.href);
  const external = isExternalHref(target.href) && target.href !== '#';

  return (
    <Link
      {...dataAttributes}
      aria-label={ariaLabel}
      className={className}
      href={href}
      onClick={onClick}
      rel={external ? 'noopener noreferrer nofollow' : undefined}
      target={external ? '_blank' : undefined}
    >
      {children}
    </Link>
  );
}
